// React is auto-imported with JSX transform
import { useState } from 'react';
import { Play } from 'lucide-react';

interface VideoEmbedProps {
  url: string;
}

export function VideoEmbed({ url }: VideoEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  // Check if URL is YouTube
  const getYouTubeEmbedUrl = (url: string): string | null => {
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubeRegex);
    
    if (match && match[1]) {
      return `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1`;
    }
    return null;
  };

  // Check if URL is Loom
  const getLoomEmbedUrl = (url: string): string | null => {
    const loomRegex = /(?:https?:\/\/)?(?:www\.)?loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/;
    const match = url.match(loomRegex);
    
    if (match && match[1]) {
      return `https://www.loom.com/embed/${match[1]}`;
    }
    return null;
  };

  // Get the appropriate embed URL
  const youtubeEmbedUrl = getYouTubeEmbedUrl(url);
  const loomEmbedUrl = getLoomEmbedUrl(url);
  const embedUrl = youtubeEmbedUrl || loomEmbedUrl;

  if (!embedUrl) {
    return null;
  }

  // Extract video ID for thumbnails
  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Get thumbnail URL
  const getThumbnailUrl = (): string => {
    const youtubeId = getYouTubeVideoId(url);
    if (youtubeId) {
      return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    }
    
    // For Loom we don't have direct thumbnail access, so use a placeholder
    return 'https://dummyimage.com/600x400/000/fff&text=Loom+Video';
  };
  
  // Handle play button click
  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div className="mt-3 rounded-lg overflow-hidden" style={{ width: '70%', maxWidth: '500px' }}>
      <div className="aspect-video w-full relative">
        {isPlaying ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allowFullScreen
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="Embedded video"
          ></iframe>
        ) : (
          <div 
            className="relative w-full h-full cursor-pointer"
            onClick={handlePlay}
          >
            <img 
              src={getThumbnailUrl()} 
              alt="Video thumbnail" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#c9fffc] text-gray-900">
                <Play className="w-8 h-8 ml-1" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
